import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { AmenityPicker } from "@/components/AmenityPicker";
import { ImageUploader } from "@/components/ImageUploader";
import { PageBackground } from "@/components/PageBackground";
import { toast } from "sonner";

export default function ProfileBuilder() {
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [areas, setAreas] = useState("");

  useEffect(() => {
    api.get("/tenant/profile").then(({ data }) => {
      setP(data);
      setAreas((data.preferred_areas || []).join(", "));
    });
  }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        budget_min: Number(p.budget_min) || null,
        budget_max: Number(p.budget_max) || null,
        preferred_areas: areas.split(",").map((a) => a.trim()).filter(Boolean),
        move_in_date: p.move_in_date,
        household_size: Number(p.household_size) || 1,
        pets: p.pets,
        employment: p.employment,
        annual_income: Number(p.annual_income) || null,
        lifestyle: p.lifestyle,
        bio: p.bio,
        home_address: p.home_address || "",
        search_radius_miles: Number(p.search_radius_miles) || 25,
        min_bedrooms: Number(p.min_bedrooms) || 0,
        min_bathrooms: Number(p.min_bathrooms) || 0,
        desired_amenities: p.desired_amenities || [],
        photo_url: p.photo_url || null,
      };
      await api.put("/tenant/profile", payload);
      toast.success("Profile saved");
      navigate("/tenant");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  if (!p) return <div className="min-h-screen grid place-items-center text-muted-foreground">Loading…</div>;
  const set = (k, v) => setP({ ...p, [k]: v });

  return (
    <div>
      <PageBackground variant={2} />
      <Navbar />
      <div className="max-w-2xl mx-auto px-6 py-10">
        <h1 className="font-display text-3xl font-semibold tracking-tight mb-1">Your rental profile</h1>
        <p className="text-muted-foreground mb-8">This is what landlords see when they pitch you.</p>
        <form onSubmit={save} className="bg-white border border-border rounded-sm p-6 space-y-5">
          <div className="flex items-center gap-4">
            <ImageUploader value={p.photo_url} onChange={(v) => set("photo_url", v)} shape="avatar" testid="pf-photo" />
            <div>
              <div className="font-medium text-sm">Profile photo</div>
              <p className="text-xs text-muted-foreground">A friendly face helps landlords connect. Optional.</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Budget min ($/mo)</Label><Input data-testid="pf-budget-min" type="number" value={p.budget_min || ""} onChange={(e) => set("budget_min", e.target.value)} /></div>
            <div><Label>Budget max ($/mo)</Label><Input data-testid="pf-budget-max" type="number" value={p.budget_max || ""} onChange={(e) => set("budget_max", e.target.value)} /></div>
          </div>
          <div><Label>Preferred areas / cities (comma separated)</Label><Input data-testid="pf-areas" value={areas} onChange={(e) => setAreas(e.target.value)} placeholder="Austin, Round Rock" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Your home / work address</Label><Input data-testid="pf-home-address" value={p.home_address || ""} onChange={(e) => set("home_address", e.target.value)} placeholder="123 Main St, Austin, TX" /></div>
            <div>
              <Label>Search radius</Label>
              <select data-testid="pf-radius" value={p.search_radius_miles || 25} onChange={(e) => set("search_radius_miles", e.target.value)}
                className="w-full h-10 rounded-sm border border-input bg-background px-3 text-sm">
                {[5, 10, 25, 50, 100].map((m) => (<option key={m} value={m}>{m} miles</option>))}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Move-in date</Label><Input data-testid="pf-movein" type="date" value={p.move_in_date || ""} onChange={(e) => set("move_in_date", e.target.value)} /></div>
            <div><Label>Household size</Label><Input data-testid="pf-household" type="number" value={p.household_size || 1} onChange={(e) => set("household_size", e.target.value)} /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Pets</Label><Input data-testid="pf-pets" value={p.pets || ""} onChange={(e) => set("pets", e.target.value)} placeholder="1 dog" /></div>
            <div><Label>Annual income ($)</Label><Input data-testid="pf-income" type="number" value={p.annual_income || ""} onChange={(e) => set("annual_income", e.target.value)} /></div>
          </div>
          <div><Label>Employment</Label><Input data-testid="pf-employment" value={p.employment || ""} onChange={(e) => set("employment", e.target.value)} placeholder="Software Engineer @ Acme" /></div>
          <div><Label>Lifestyle preferences</Label><Input data-testid="pf-lifestyle" value={p.lifestyle || ""} onChange={(e) => set("lifestyle", e.target.value)} placeholder="Non-smoker, quiet, WFH" /></div>
          <div><Label>Short bio</Label><Textarea data-testid="pf-bio" value={p.bio || ""} onChange={(e) => set("bio", e.target.value)} rows={3} /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Minimum bedrooms</Label><Input data-testid="pf-min-bedrooms" type="number" min="0" value={p.min_bedrooms ?? 0} onChange={(e) => set("min_bedrooms", e.target.value)} /></div>
            <div><Label>Minimum bathrooms</Label><Input data-testid="pf-min-bathrooms" type="number" min="0" value={p.min_bathrooms ?? 0} onChange={(e) => set("min_bathrooms", e.target.value)} /></div>
          </div>
          <div>
            <Label>Must-have amenities</Label>
            <p className="text-xs text-muted-foreground mb-2">You'll only be matched with places that have every amenity you select.</p>
            <AmenityPicker value={p.desired_amenities || []} onChange={(v) => set("desired_amenities", v)} testidPrefix="pf-amenity" />
          </div>
          <Button type="submit" data-testid="pf-save" className="w-full">Save profile</Button>
        </form>
      </div>
    </div>
  );
}
